import {FieldNames} from "models/fields";
import {ASSIGNMENT_MAP, FIELD_WITHOUT_VALUE, STATUS_MAP} from "models/options";

/**
 *
 * @param {Object} response Response Object
 * @param {string} header Which header value to return.
 * @returns {string} Value of chose header.
 */
export const extractHeaderValue = (response, header) =>
  response.headers[header];

/**
 *
 * @param {Object} response Response Object
 * @returns {number} Value of x-total-hits header.
 */
export const extractXHitsHeader = (response) =>
  parseInt(extractHeaderValue(response, "x-total-hits"));

/**
 * Parse a failed XHR response error object, produce an error string
 * for UI display.
 * @param {Object} results The Error result object to parse.
 * @returns {String} Error message for UI.
 */
export const getAPIErrorMessages = (results) => {
  const messages = [];
  if (results && results.errors) {
    results.errors.forEach(function (item) {
      const {module, message} = item;
      messages.push(`${module} ${message}`);
    });
  } else {
    if (results && results.error) {
      messages.push(
        results.error.message ||
          results.message ||
          "Unexpected error occurred, see log for details"
      );
    } else {
      console.error("Unexpected error:", results);
      messages.push("Unexpected error occurred, see log for details");
    }
  }
  return messages.join(" ");
};

const hasFilters = (selected, filters) =>
  selected.length > 0 && selected.length < Object.keys(filters).length;

export const buildAssigneeQuery = (assigneeFilter, userId) =>
  hasFilters(assigneeFilter, ASSIGNMENT_MAP)
    ? buildHasAssigneeQuery(assigneeFilter, userId)
    : "";
/**
 * Builds Assignee Query String
 *
 * @param {Array} assigneeFilter Array of selected Assignee values.
 * @param {string} userId Current User ID.
 * @returns query string.
 */
export const buildHasAssigneeQuery = (assigneeFilter, userId) => {
  if (assigneeFilter.includes(ASSIGNMENT_MAP.Self.value)) {
    if (assigneeFilter.includes(ASSIGNMENT_MAP.Others.value)) {
      return `${FieldNames.assignees}:(*)`;
    } else if (assigneeFilter.includes(ASSIGNMENT_MAP.Unassigned.value)) {
      return `!${FieldNames.assignees}:(* || !"${userId}")`;
    } else {
      return `${FieldNames.assignees}:("${userId}")`;
    }
  } else if (assigneeFilter.includes(ASSIGNMENT_MAP.Others.value)) {
    if (assigneeFilter.includes(ASSIGNMENT_MAP.Unassigned.value)) {
      return `!${FieldNames.assignees}:("${userId}")`;
    } else {
      return `${FieldNames.assignees}:(* || !"${userId}")`;
    }
  } else {
    return `!${FieldNames.assignees}:(*)`;
  }
};

/**
 * Builds Status Query String
 *
 * @param {Array} statusFilter Array of selected statusFilters
 * @returns {string} Query String
 */
export const buildStatusQuery = (statusFilter) =>
  handleQuery(FieldNames.status, statusFilter, STATUS_MAP);

/**
 * Builds queries based on filter values
 *
 * @param {string} fieldName name of field
 * @param {Array} filterState array of current set filters
 * @param {Object} fieldOptions filter options available for field
 * @returns {string} fully built query with field and values
 */
export const handleQuery = (fieldName, filterState, fieldOptions) => {
  const query =
    Array.isArray(filterState) &&
    filterState.length > 0 &&
    filterState
      .map((key, index) => {
        const queryValue = handleQueryValues(filterState, fieldOptions, key);

        if (key !== FIELD_WITHOUT_VALUE.NotSpecified.value) {
          return index < filterState.length - lastFilterKeyIndex(filterState)
            ? `${queryValue} || `
            : `${queryValue}`;
        }
      })
      .join("");

  return query ? handleQueryWrapper(fieldName, filterState, query) : "";
};

/**
 * Formats query values
 *
 * @param {Object} fieldOptions filter options available for field
 * @param {Object} obj filter option
 * @param {string} key filter option key
 * @returns {string} query to either include or not include value
 */
const handleQueryValues = (fieldOptions, obj, key) =>
  fieldOptions.includes(FIELD_WITHOUT_VALUE.NotSpecified.value)
    ? `!"${obj[key].value}"`
    : `"${obj[key].value}"`;

/**
 * Checks if empty value option exists to determine last index for filter query,
 * necessary for optional fields.
 *
 * @param {Object} filters filter options available for field
 * @returns {number} last index to stop concatenating "&&" or "||"
 */
export const lastFilterKeyIndex = (filters) =>
  filters.includes(FIELD_WITHOUT_VALUE.NotSpecified.value) ? 2 : 1;

/**
 * Gives correct query prefix based on field being optional/required
 *
 * @param {string} field field name
 * @param {Array} filterState array of current set filters
 * @param {string} queryValues lucene build string of values to include/not include
 * @returns {string} Query prefix to either include/not empty fields (for optional fields)
 */
const handleQueryWrapper = (field, filterState, queryValues) =>
  filterState.includes(FIELD_WITHOUT_VALUE.NotSpecified.value)
    ? `!${field}:(* || ${queryValues})`
    : `${field}:(${queryValues})`;
